import { useQuery } from '@apollo/client';
import previewSrc from '@fanconnect/neo-client/dist/contentItem.html?url';
import { DriveFolderUpload, FolderZip } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  styled,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCreateTemplate } from '~/api/templates';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import {
  DialogActions,
  DialogContent,
  DialogPrimaryTextField,
} from '~/components/dialogs/lib/styles';
import { StoppablePreview } from '~/components/preview';
import { useAppContext } from '~/contexts';
import { assert } from '~/lib/assert';
import { toDataUri } from '~/lib/file';
import { formatBytes } from '~/lib/units';
import {
  AddAppDialogDocument,
  TemplateZipValidationDocument,
} from './create-template-dialog.generated';
import { useTemplateZipPreview } from './lib/useTemplateZipPreview';

const AppCategorySelect = styled(Select)({
  minWidth: '35%',
  height: '58px',
  top: '8px',
  left: '5px',
});

const AppsFieldWrapper = styled('div')({ display: 'flex' });

/** Used when `config.json` omits `aspectRatio`, i.e. a responsive template. */
const DEFAULT_PREVIEW_RATIO = '16/9';

/** Caps how tall the preview can get, so portrait templates still fit. */
const PREVIEW_MAX_HEIGHT = '320px';

/** `my-cool-template.zip` -> `My Cool Template` */
const nameFromFilename = (filename: string) =>
  filename
    .replace(/\.zip$/i, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

export interface AddAppDialogProps {
  readonly open: boolean;
  setOpen: (value: boolean) => void;
}

export const AddAppDialog = ({ open, setOpen }: AddAppDialogProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [templateCategoryId, setTemplateCategoryId] = useState<number | ''>('');
  const [file, setFile] = useState<File>();
  const [dataUrl, setDataUrl] = useState<string>();

  const navigate = useNavigate();

  const { currentNetwork } = useAppContext();

  const { data } = useQuery(AddAppDialogDocument);

  const [create, createState] = useCreateTemplate();

  const onDrop = useCallback(
    (accepted: File[]) => {
      const dropped = accepted.at(0);
      if (!dropped) return;

      setFile(dropped);
      // Only prefill an untouched name so a re-upload doesn't clobber an edit.
      setName((current) => (current.trim() === '' ? nameFromFilename(dropped.name) : current));
    },
    [setFile, setName],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: ['.zip', 'application/zip', 'application/x-zip-compressed'],
    minSize: 1,
    multiple: false,
    onDrop,
  });

  // Encoded once per file and shared by validation and Create, rather than
  // encoding the same zip twice.
  useEffect(() => {
    if (file == null) return setDataUrl(undefined);

    let cancelled = false;
    void toDataUri(file).then((encoded) => {
      if (!cancelled) setDataUrl(encoded);
    });

    return () => {
      cancelled = true;
    };
  }, [file]);

  // The server is the only thing that validates a zip, so the dialog asks it
  // rather than duplicating the rules — see ValidateTemplateZipService.
  const { data: validation, loading: validating } = useQuery(TemplateZipValidationDocument, {
    skip: dataUrl == null,
    variables: { dataUrl: dataUrl ?? '', networkId: currentNetwork.id },
  });

  const validationErrors = validation?.templateZipValidation.errors ?? [];

  const zipPreview = useTemplateZipPreview(file, name);

  const nameValid = name.trim() !== '';
  const canCreate =
    dataUrl != null &&
    nameValid &&
    templateCategoryId !== '' &&
    !validating &&
    validationErrors.length === 0;

  const onCreate = async () => {
    assert(dataUrl != null, 'Create template: no zip selected');
    assert(nameValid, 'Create template: name is blank');
    assert(typeof templateCategoryId === 'number', 'Create template: no category selected');

    const { data } = await create({
      variables: { input: { dataUrl, name, networkId: currentNetwork.id, templateCategoryId } },
    });

    if (data?.createTemplate?.template?.id) {
      setOpen(false);
      return navigate(`${data.createTemplate.template.id}/settings`);
    }

    // Anything that gets past validation and still fails is reported by the
    // mutation's error toast.
    createState.reset();
  };

  const onExited = useCallback(() => {
    setName('');
    setTemplateCategoryId('');
    setFile(undefined);
  }, []);

  /**
   * Before a zip is chosen the drop zone is the primary action and gets the
   * room. Once one is chosen its only job is to say what's loaded and offer to
   * replace it, so it collapses to a bar and the preview takes the full width.
   */
  const dropzone = useMemo(
    () => (
      <Box
        {...getRootProps()}
        sx={{
          alignItems: 'center',
          bgcolor: 'grey.50',
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          cursor: 'pointer',
          display: 'flex',
          '&:hover': { borderColor: 'primary.main' },
          ...(file
            ? { gap: 1.5, px: 2, py: 1.5 }
            : { flexDirection: 'column', justifyContent: 'center', py: 5, textAlign: 'center' }),
        }}
      >
        <input {...getInputProps()} />
        {file ? (
          <>
            <FolderZip sx={{ color: 'primary.main', flexShrink: 0, fontSize: 28 }} />
            <Typography
              variant="body1"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {t('pages.templates.create_dialog.replace_zip_message', {
                size: formatBytes(file.size),
              })}
            </Typography>
          </>
        ) : (
          <>
            <DriveFolderUpload sx={{ fontSize: 64, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="h5">
              {t('pages.templates.create_dialog.drop_zip_message')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('pages.templates.create_dialog.browse_zip_message')}
            </Typography>
          </>
        )}
      </Box>
    ),
    [file, getInputProps, getRootProps, t],
  );

  // Flag the outstanding required fields, but only once a zip is chosen — the
  // dialog opens empty and shouldn't greet the user in red.
  const showRequired = file != null;

  const fields = (
    <AppsFieldWrapper>
      <DialogPrimaryTextField
        autoFocus
        disabled={createState.loading}
        error={showRequired && !nameValid}
        fullWidth
        inputProps={{ 'aria-label': t('pages.templates.create_dialog.name_label') }}
        margin="dense"
        onChange={(event) => setName(event.target.value)}
        placeholder={t('pages.templates.create_dialog.name_placeholder')}
        value={name}
      />
      <AppCategorySelect
        disabled={createState.loading}
        displayEmpty
        error={showRequired && templateCategoryId === ''}
        inputProps={{ 'aria-label': t('pages.templates.create_dialog.category_placeholder') }}
        margin="dense"
        onChange={(event) => {
          assert(typeof event.target.value === 'number');
          setTemplateCategoryId(event.target.value);
        }}
        value={templateCategoryId}
      >
        <MenuItem value="" disabled>
          {t('pages.templates.create_dialog.category_placeholder')}
        </MenuItem>
        {data?.templateCategories.map(({ id, name }) => (
          <MenuItem key={id} value={id}>
            {name}
          </MenuItem>
        ))}
      </AppCategorySelect>
    </AppsFieldWrapper>
  );

  // Only ever describes the preview surface itself. Why a zip is unusable is
  // reported in the alert below, so problems appear in exactly one place.
  const previewMessage = zipPreview.unavailable
    ? t('pages.templates.create_dialog.preview_unavailable')
    : zipPreview.loading
    ? t('components.preview.loading_text')
    : t('pages.templates.create_dialog.preview_placeholder');

  // Templates declare their own aspect ratio and can be any shape — portrait
  // 9/16 is as valid as 16/9. Bounding the height and letting width follow the
  // ratio keeps a tall template from overflowing the dialog.
  const previewRatio = zipPreview.aspectRatio ?? DEFAULT_PREVIEW_RATIO;
  const previewBounds = {
    maxWidth: `calc(${PREVIEW_MAX_HEIGHT} * calc(${previewRatio}))`,
    mt: 2,
    mx: 'auto',
    width: '100%',
  };

  // Nothing to preview until a zip is chosen, so the drop zone gets the dialog
  // to itself rather than sitting beside an empty frame.
  const preview =
    file == null ? null : zipPreview.payload == null ? (
      <Box
        sx={{
          ...previewBounds,
          alignItems: 'center',
          aspectRatio: previewRatio,
          bgcolor: 'grey.100',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'center',
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {previewMessage}
        </Typography>
      </Box>
    ) : (
      <Box sx={previewBounds}>
        <StoppablePreview
          aspectRatio={previewRatio}
          disableMinHeight
          payload={zipPreview.payload}
          src={previewSrc}
        />
      </Box>
    );

  // The preview is centred and capped by PREVIEW_MAX_HEIGHT rather than by a
  // column, so `md` keeps it comfortably inset without leaving a wide empty
  // margin either side of it.
  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={() => setOpen(false)}
      open={open}
      TransitionProps={{ onExited }}
    >
      <DialogTitle onClose={() => setOpen(false)}>
        {t('pages.templates.create_dialog.title')}
      </DialogTitle>

      <DialogContent>
        <DialogContentTitle>{t('pages.templates.create_dialog.description')}</DialogContentTitle>

        {/* Errors sit above the form, where they're seen before anything else. */}
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 1, mt: 1 }}>
            <AlertTitle>{t('pages.templates.create_dialog.config_errors_title')}</AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {validationErrors.map((error, index) => (
                <li key={`${error.path.join('.')}-${index}`}>
                  {error.path.length > 0 && (
                    <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                      {error.path.join('.')}
                      {': '}
                    </Typography>
                  )}
                  <Typography component="span" variant="body2">
                    {error.explanation.join(' ')}
                  </Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {fields}

        <Box sx={{ mt: 2 }}>{dropzone}</Box>

        {preview}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={() => setOpen(false)}>
          {t('common.dialogs.cancel')}
        </Button>
        <LoadingButton
          color="primary"
          variant="contained"
          loading={createState.loading}
          disabled={!canCreate}
          onClick={() => void onCreate()}
        >
          {t('common.dialogs.create')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export const useCreateTemplateDialog = () => {
  const [open, setOpen] = useState(false);

  const props = useMemo(() => ({ open, setOpen }), [open]);

  const openDialog = useCallback(() => {
    setOpen(true);
  }, []);

  return [openDialog, props] as const;
};
